import React, { useState, useEffect, useCallback } from 'react';
import {
  DndContext,
  useDroppable,
  useDraggable,
  closestCenter,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { RiDeleteBin6Line } from 'react-icons/ri';
import { addBadPoster, isBadPoster } from '../utils/badPosterCache';
import { FaTimes } from 'react-icons/fa';

// Define card size type
type CardSize = 'small' | 'medium' | 'large';

// Define DEFAULT_TIERS here (needed for clearing)
const DEFAULT_TIERS = [
  { name: 'S', color: '#22c55e', items: [] },
  { name: 'A', color: '#a3e635', items: [] },
  { name: 'B', color: '#fde047', items: [] },
  { name: 'C', color: '#fdba74', items: [] },
  { name: 'D', color: '#ef4444', items: [] },
];

interface TierListProps {
  tiers: any[];
  setTiers: (tiers: any[]) => void;
  searchResults: any[];
  setSearchResults: (items: any[]) => void;
  activeDrag: any;
  setActiveDrag: (item: any) => void;
  cardSize: CardSize;
  setCardSize: (size: CardSize) => void;
  isTapInterface?: boolean;
  onTapItem?: (item: any, tierIdx: number, itemIdx: number, position: { x: number, y: number }) => void;
  useTapInterfaceOverride?: boolean;
  setUseTapInterfaceOverride?: (value: boolean) => void;
  hideInteractionControls?: boolean;
  isReadOnly?: boolean;
}

interface PosterSortableProps {
  item: any;
  cardSize: CardSize;
  onDragStart?: (info: { item: any; tierIdx?: number; itemIdx?: number }) => void;
  tierIdx?: number;
  itemIdx?: number;
  isTapInterface?: boolean;
  onTapItem?: (item: any, tierIdx: number, itemIdx: number, position: { x: number, y: number }) => void;
  isReadOnly?: boolean;
}

// ---> Start Image Error Handling Hook (Copied from ImdbSearch.tsx) <---
const useImageErrorHandler = (posterUrl: string | undefined | null) => {
  const [imageError, setImageError] = useState(false);

  // Reset error state when the poster URL changes
  useEffect(() => {
    setImageError(false);
  }, [posterUrl]);

  const handleImageErrorCallback = useCallback(() => {
    console.warn(`[TierList Image Error] Failed to load: ${posterUrl}`); // Add logging
    setImageError(true);
    if (posterUrl) {
      addBadPoster(posterUrl);
    }
  }, [posterUrl]);

  return { imageError, handleImageError: handleImageErrorCallback }; // Rename handler to avoid potential naming conflicts
};
// ---> End Image Error Handling Hook <---

function PosterSortable({ 
  item, 
  cardSize, 
  onDragStart, 
  tierIdx, 
  itemIdx, 
  isTapInterface = false,
  onTapItem,
  isReadOnly = false
}: PosterSortableProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, over } = useSortable({
    id: `tier-item-${item.imdbID}`,
    data: {
      item: item,
      tierIdx: tierIdx,
      itemIdx: itemIdx
    },
    disabled: isTapInterface || isReadOnly,
  });

  // ---> Use the image error handling hook <---
  const { imageError, handleImageError } = useImageErrorHandler(item.Poster);
  const isKnownBad = isBadPoster(item.Poster);
  const hasValidPoster = !imageError && !isKnownBad && item.Poster && item.Poster !== 'N/A';

  // Reset state when item changes
  React.useEffect(() => {
    // No longer need to manage posterUrl/retryCount state here
  }, [item.imdbID, item.Poster]);

  // Handle drag start
  React.useEffect(() => {
    if (isDragging && onDragStart && tierIdx !== undefined && itemIdx !== undefined) {
      onDragStart({ 
        item, 
        tierIdx, 
        itemIdx 
      });
    }
  }, [isDragging, item, onDragStart, tierIdx, itemIdx]);

  const sizeClasses: Record<CardSize, string> = {
    small: "w-[72px] h-[107px]",
    medium: "w-[96px] h-[142px]",
    large: "w-[120px] h-[178px]",
  };
  
  // Handle tap for mobile interface
  const handleTap = (e: React.MouseEvent) => {
    if (isTapInterface && !isReadOnly && onTapItem && tierIdx !== undefined && itemIdx !== undefined) {
      e.preventDefault();
      e.stopPropagation();
      
      // Get position for popup
      const rect = e.currentTarget.getBoundingClientRect();
      const position = { 
        x: rect.left + rect.width / 2, 
        y: rect.top + rect.height / 2 
      };
      
      // Call onTapItem directly without delay
      onTapItem(item, tierIdx, itemIdx, position);
    }
  };

  // Check if item is being dragged over
  const isBeingDraggedOver = over && over.id === `tier-item-${item.imdbID}`;
  
  // Determine if we should show left or right indicator based on cursor position
  const isLeftSide = over && over.id === `tier-item-${item.imdbID}` && 
    over.data?.current?.draggingOver?.rect?.left < over.rect.left + (over.rect.width / 2);

  return (
    <div
      ref={setNodeRef}
      {...(isTapInterface || isReadOnly ? {} : attributes)}
      {...(isTapInterface || isReadOnly ? {} : listeners)}
      onClick={handleTap}
      className={`flex flex-col items-center justify-start bg-gray-700 rounded shadow ${
        isTapInterface && !isReadOnly ? 'hover:opacity-80 active:opacity-70 transition-opacity cursor-pointer' : 
        (!isReadOnly ? 'hover:shadow-md' : 'cursor-not-allowed') 
      } ${sizeClasses[cardSize]} ${isBeingDraggedOver ? 'z-20' : ''}`}
      style={{
        transform: isDragging ? `translate3d(${transform?.x}px, ${transform?.y}px, 0)` : undefined,
        transition: 'none',
        opacity: isDragging ? 0.5 : 1,
        position: 'relative',
        zIndex: isBeingDraggedOver ? 20 : 'auto',
        boxShadow: isBeingDraggedOver ? '0 0 0 3px #3b82f6, 0 4px 8px rgba(0,0,0,0.3)' : undefined,
      }}
    >
      {/* Restore indicators but use absolute positioning to avoid causing sliding */}
      {/* Left indicator */}
      {isBeingDraggedOver && !isDragging && isLeftSide && (
        <div className="absolute -left-1 top-1/2 w-3 h-24 bg-blue-500 rounded-sm transform -translate-y-1/2 z-30" 
             style={{ pointerEvents: 'none' }}></div>
      )}
      
      {/* Right indicator */}
      {isBeingDraggedOver && !isDragging && !isLeftSide && (
        <div className="absolute -right-1 top-1/2 w-3 h-24 bg-blue-500 rounded-sm transform -translate-y-1/2 z-30"
             style={{ pointerEvents: 'none' }}></div>
      )}
      
      {/* Poster image - Use hasValidPoster check */}
      {hasValidPoster ? (
        <img
          src={item.Poster}
          alt={item.Title}
          className="w-full h-full object-cover poster-img"
          onError={handleImageError}
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-gray-600 text-gray-400 text-xs text-center p-2">
          {item.Title || 'No Poster'}
        </div>
      )}
    </div>
  );
}

export default function TierList({ 
  tiers, 
  setTiers, 
  searchResults, 
  setSearchResults, 
  activeDrag, 
  setActiveDrag, 
  cardSize, 
  setCardSize,
  isTapInterface = false,
  onTapItem,
  isReadOnly = false
}: TierListProps) {
  const [activeTier, setActiveTier] = React.useState<number | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [tierListTapMode, setTierListTapMode] = useState(false);

  // ---> Calculate effective mode: Force tap on touch, else use desktop preference <---
  const effectiveTierListTapMode = isTapInterface || tierListTapMode;

  // All poster IDs in all tiers (for uniqueness)
  const allTierIds = tiers.flatMap(tier => tier.items.map((item: any) => item.imdbID));

  // Reset tiers function
  const handleClearTiers = () => {
    // Confirmation will be handled inline now
    setTiers(DEFAULT_TIERS.map(t => ({ ...t, items: [] })));
  };

  return (
    <div className="bg-gray-800 p-4 sm:p-6 rounded-lg shadow-md">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
        <h2 className="text-xl font-bold text-white">Your Tier List</h2>
        {!isReadOnly && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-300">Card size:</span>
              <select
                className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-sm text-white"
                value={cardSize}
                onChange={(e) => setCardSize(e.target.value as CardSize)}
                disabled={isReadOnly}
              >
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
              </select>
            </div>
            
            {/* ---> Conditionally show Interaction Mode Selector ONLY on non-touch devices <--- */}
            {!isTapInterface && (
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray-300">Interaction:</span>
                <label className="flex items-center gap-1 cursor-pointer text-sm text-gray-300">
                  <input 
                    type="radio" 
                    name="tierListInteractionMode" 
                    value="drag" 
                    checked={!tierListTapMode} 
                    onChange={() => setTierListTapMode(false)} 
                    className="form-radio h-3.5 w-3.5 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
                    disabled={isReadOnly}
                  />
                  Drag
                </label>
                <label className="flex items-center gap-1 cursor-pointer text-sm text-gray-300">
                  <input 
                    type="radio" 
                    name="tierListInteractionMode" 
                    value="tap" 
                    checked={tierListTapMode} 
                    onChange={() => setTierListTapMode(true)} 
                    className="form-radio h-3.5 w-3.5 text-blue-600 bg-gray-700 border-gray-600 focus:ring-blue-500"
                    disabled={isReadOnly}
                  />
                  Tap
                </label>
              </div>
            )}
            {/* ---> END Conditional Interaction Mode Selector <--- */}
            
            <button
              onClick={() => setShowConfirm(true)}
              className="flex items-center gap-1.5 px-2 py-0.5 rounded bg-red-700 hover:bg-red-800 text-white text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Clear all items from all tiers"
              disabled={isReadOnly}
            >
              <RiDeleteBin6Line size={12}/> 
              <span>Clear Tiers</span>
            </button>
          </div>
        )}
      </div>
      
      {/* Mobile Help Text - Use effective mode */}
      {effectiveTierListTapMode && (
        <div className="mb-4 text-sm text-blue-400 bg-blue-900 bg-opacity-20 p-2 rounded">
          <p>Tap on a poster to choose which tier to move it to, or reorder it within its current tier.</p>
        </div>
      )}
      
      <div className="space-y-2">
        {tiers.map((tier, tierIdx) => (
          <DroppableTier
            key={`tier-${tierIdx}`}
            tier={tier}
            tierIdx={tierIdx}
            activeDrag={activeDrag}
            activeTier={activeTier}
            allTierIds={allTierIds}
            cardSize={cardSize}
            onDragStart={setActiveDrag}
            isTapInterface={effectiveTierListTapMode}
            onTapItem={onTapItem}
            isReadOnly={isReadOnly}
          />
        ))}
        
        {/* Trash Bin - Use effective mode */}
        {!effectiveTierListTapMode && !isReadOnly && activeDrag && (
          <TrashBin activeDrag={activeDrag} isTapInterface={effectiveTierListTapMode} />
        )}
      </div>
      
      {showConfirm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50">
          <div className="bg-gray-700 rounded-lg p-6 shadow-xl flex flex-col items-center">
            <div className="mb-4 text-lg font-semibold text-gray-200">Are you sure you want to clear your entire tier list?</div>
            <div className="flex gap-4">
              <button
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
                onClick={() => {
                  setTiers(DEFAULT_TIERS.map(tier => ({ ...tier, items: [] })));
                  // Don't clear search results or localStorage
                  // Just reset the tier list
                  setShowConfirm(false);
                }}
              >
                Yes, clear
              </button>
              <button
                className="bg-gray-600 text-gray-200 px-4 py-2 rounded hover:bg-gray-500"
                onClick={() => setShowConfirm(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface DroppableTierProps {
  tier: any;
  tierIdx: number;
  activeDrag: any;
  activeTier: number | null;
  allTierIds: string[];
  cardSize: CardSize;
  onDragStart?: (info: { item: any; tierIdx?: number; itemIdx?: number }) => void;
  isTapInterface?: boolean;
  onTapItem?: (item: any, tierIdx: number, itemIdx: number, position: { x: number, y: number }) => void;
  isReadOnly?: boolean;
}

function DroppableTier({ 
  tier, 
  tierIdx, 
  activeDrag, 
  activeTier, 
  allTierIds, 
  cardSize, 
  onDragStart,
  isTapInterface = false,
  onTapItem,
  isReadOnly = false
}: DroppableTierProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: `tier-${tierIdx}`,
    disabled: isReadOnly,
    data: {
      tierIdx: tierIdx,
    },
  });

  const minHeightClass: Record<CardSize, string> = {
    small: "min-h-[120px]",
    medium: "min-h-[150px]",
    large: "min-h-[200px]",
  };

  return (
    <SortableContext
      id={`tier-${tierIdx}`}
      items={tier.items.map((item: any) => `tier-item-${item.imdbID}`)}
      strategy={rectSortingStrategy}
      disabled={isReadOnly}
    >
      <div
        ref={setNodeRef} 
        className={`rounded-lg flex flex-col md:flex-row items-stretch gap-0 w-full overflow-hidden ${
          isOver ? 'border-4 border-blue-600 shadow-lg' : 'border-transparent border-4'
        }`}
        style={{ 
          background: tier.color,
          boxShadow: isOver ? '0 0 0 4px #3b82f6, 0 4px 24px 0 rgba(59,130,246,0.15)' : undefined,
          transition: 'none',
          transform: 'none',
        }}
      >
        <div className="flex-shrink-0 md:w-12 w-full flex justify-center items-center font-bold text-2xl bg-black bg-opacity-10 py-2 md:py-0">
          <span 
            className="font-bold text-2xl md:text-3xl lg:text-4xl text-white"
            style={{ textShadow: '1px 1px 3px rgba(0,0,0,0.8)' }}
          >
            {tier.name}
          </span>
        </div>
        
        <div className={`flex-grow flex flex-wrap gap-3 items-start z-10 min-h-[1px] overflow-y-auto py-3 px-3 sortable-items-container ${
          isOver ? 'bg-white bg-opacity-10' : ''
        }`}>
          {tier.items.map((item: any, itemIdx: number) => (
            <PosterSortable 
              key={item.imdbID} 
              item={item} 
              cardSize={cardSize} 
              onDragStart={onDragStart} 
              tierIdx={tierIdx} 
              itemIdx={itemIdx}
              isTapInterface={isTapInterface}
              onTapItem={onTapItem}
              isReadOnly={isReadOnly}
            />
          ))}
          {tier.items.length === 0 && (
            <div className={`flex-grow h-full flex items-center justify-center text-gray-400 italic min-h-[50px] pointer-events-none ${isOver ? 'text-blue-400 font-semibold' : ''}`}>
              {isOver ? 'Drop here!' : 'No items yet'}
            </div>
          )}
        </div>
        
        <div className="flex-shrink-0 flex items-center justify-center px-3 py-1 md:py-0 text-sm font-medium whitespace-nowrap bg-black bg-opacity-10 w-full md:w-auto">
          {tier.items.length} items
        </div>
      </div>
    </SortableContext>
  );
}

interface PosterOverlayProps {
  id: string;
  tiers: any[];
  searchResults: any[];
  cardSize: CardSize;
}

function PosterOverlay({ id, tiers, searchResults, cardSize }: PosterOverlayProps) {
  let item = null;
  for (const tier of tiers) {
    item = tier.items.find((i: any) => i.imdbID === id);
    if (item) break;
  }
  if (!item) {
    item = searchResults.find((i: any) => i.imdbID === id);
  }
  if (!item) return null;

  const sizeClasses: Record<CardSize, string> = {
    small: "w-[68px] h-[101px]",
    medium: "w-[90px] h-[135px]",
    large: "w-[113px] h-[169px]",
  };

  return (
    <div className={`flex flex-col items-center justify-start bg-white rounded shadow mx-auto ${sizeClasses[cardSize]}`}>
      <img
        src={item.Poster}
        alt={item.Title}
        className={`object-cover bg-white rounded ${sizeClasses[cardSize]}`}
      />
    </div>
  );
}

function TrashBin({ activeDrag, isTapInterface }: { activeDrag: any, isTapInterface: boolean }) {
  const { setNodeRef, isOver } = useDroppable({
    id: 'trash',
    disabled: isTapInterface,
  });
  
  const showTrash = activeDrag && !isTapInterface;

  return (
    <div 
      ref={setNodeRef}
      className={`mt-4 flex items-center justify-center p-3 border-2 border-dashed ${
        showTrash ? 'border-red-600 bg-red-600 bg-opacity-10' : 'border-gray-700'
      } rounded text-gray-400 text-sm transition-colors duration-200`}
    >
      {showTrash ? (
        <div className="flex items-center">
          <RiDeleteBin6Line className={`mr-2 text-xl ${isOver ? 'text-red-400' : 'text-red-500'}`} />
          <span>{isTapInterface ? "Use item popup menu to remove" : "Drop here to remove from tier"}</span>
        </div>
      ) : (
        <div className="flex items-center">
          <RiDeleteBin6Line className="mr-2 text-xl" />
          <span>{isTapInterface ? "Tap a poster to move or remove it" : "Drag items here to remove from tiers"}</span>
        </div>
      )}
    </div>
  );
}

export {} 